import {supabase} from './services/supabase';
import type {
    TAnnualUpgradeFunctionResponse,
    TAnnualUpgradePlan,
    TSubscriptionChangeBillingCycle,
} from '/src/components/pricing/AnnualSubscriptionUpgrade';
import type {ReferralSummary} from '/src/components/referrals/referrals';

interface User {
    id: number;
    name: string;
    email: string;
}

export const getUsers = async (): Promise<User[]> => {
    const {data, error} = await supabase.from('users').select('*');
    if (error) throw error;
    return data as User[];
};

export const createUser = async (userData: Omit<User, 'id'>): Promise<User> => {
    const {data, error} = await supabase.from('users').insert(userData).select().single();
    if (error) throw error;
    return data as User;
};

export const getUserSubscription = async (userId: string): Promise<any> => {
    const {data, error} = await supabase.from('subscriptions').select('*').eq('user_id', userId).maybeSingle();
    if (error) {
        console.error('Error getting subs:', error);
        return;
    }
    return data;
};

export const getReferralSummary = async (): Promise<ReferralSummary> => {
    const {data, error} = await supabase.rpc('get_referral_summary');
    if (error) throw error;
    return data as ReferralSummary;
};

export const acceptReferral = async (invitationCode: string): Promise<{accepted: boolean; reason?: string}> => {
    const {data, error} = await supabase.rpc('accept_referral', {invitation_code: invitationCode});
    if (error) throw error;
    return data as {accepted: boolean; reason?: string};
};

export const submitPublication = async (postUrl: string): Promise<'submitted' | 'already_submitted'> => {
    const {error} = await supabase.functions.invoke('publication_submit', {
        method: 'POST',
        body: {post_url: postUrl},
    });
    if ((error as {context?: Response} | null)?.context?.status === 409) return 'already_submitted';
    if (error) throw error;
    return 'submitted';
};

export const getSubscriptionData = async (id: number): Promise<any> => {
    const {data, error} = await supabase.functions.invoke(`subscription?id=${id}`, {
        method: 'GET',
    });
    if (error) {
        console.error('Error getting subscription:', error);
        throw error;
    }
    return data;
};

export const getPaddlePortal = async (customerId: string): Promise<any> => {
    // Encode customer id before passing it in the query string.
    const encodedCustomerId = encodeURIComponent(customerId);
    const {data, error} = await supabase.functions.invoke(`paddle_portal?id=${encodedCustomerId}`, {
        method: 'GET',
    });
    if (error) {
        console.error('Error getting subscription:', error);
        throw error;
    }
    return data;
};

export const saveCustomerSurvey = async (answers: Record<string, any>, details: string): Promise<any> => {
    const {data, error} = await supabase.functions.invoke('customer_survey', {
        method: 'POST',
        body: {answers, details},
    });
    if (error) {
        console.error('Error saving survey:', error);
        throw error;
    }
    return data;
};

export const sendPaddleUpgradeRequest = async (upgradeData: Record<string, any>): Promise<any> => {
    // Invoke edge function that forwards manual upgrade request details via e-mail.
    const {data, error} = await supabase.functions.invoke('request_upgrade_form', {
        method: 'POST',
        body: upgradeData,
    });
    if (error) {
        console.error('Error sending upgrade request:', error);
        throw error;
    }
    return data;
};

export type TAnnualSubscriptionUpgradeAction = 'preview' | 'confirm';

export const requestAnnualSubscriptionUpgrade = async (
    action: TAnnualSubscriptionUpgradeAction,
    plan: TAnnualUpgradePlan,
    cycle: TSubscriptionChangeBillingCycle = 'yearly',
    offerCode?: string,
): Promise<TAnnualUpgradeFunctionResponse> => {
    // Invoke edge function that updates the existing Paddle subscription.
    const {data, error} = await supabase.functions.invoke('subscription_change', {
        method: 'POST',
        body: {action, plan, cycle, offerCode},
    });
    if (error) {
        console.error('Error upgrading subscription billing cycle:', error);
        throw error;
    }
    return data as TAnnualUpgradeFunctionResponse;
};
